// cacheClient.ts

interface SaveToCacheRequest<TBody = unknown> {
    RestaurantCode: string;
    ObjectType: string;
    UserKey?: string | null;
    ExpirySeconds: number;
    Body: TBody;
}

interface CacheKeyRequest {
    RestaurantCode: string;
    ObjectType: string;
}

async function postJson<TResponse = unknown, TRequest = unknown>(
    url: string,
    payload?: TRequest
): Promise<TResponse> {
    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: payload != null ? JSON.stringify(payload) : null
    });

    if (!response.ok) {
        // Let callers handle status-specific behaviour if they need to
        throw new Error(`Request to ${url} failed with status ${response.status}`);
    }

    // Some endpoints (ClearCache) may return empty body
    const contentType = response.headers.get("Content-Type") || "";
    if (!contentType.includes("application/json")) {                  
        return undefined as TResponse;
    }

    return (await response.json()) as TResponse;
}

const CacheClient = {
    /**
     * Save something to the session cache.
     * @param resCode Restaurant / venue code
     * @param objectType Logical type name e.g. "RestaurantDetails", "Menu", "UserProfile"
     * @param objectToSave Any serializable value
     * @param expirySeconds Time before object expires in cache
     */
    async SaveToCache<TBody = unknown>(
        resCode: string,
        objectType: string,
        objectToSave: TBody,
        expirySeconds: number
    ): Promise<void> {

        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to save object to cache. Missing ResCode`);
        }

        if (objectType.trim() === "" || !objectType || objectType === null || objectType === undefined)
        {
            throw new Error(`Failed to save object to cache. Missing ObjectType`);
        }

        if (!objectToSave || objectToSave === null || objectToSave === undefined)
        {
            throw new Error(`Failed to save object to cache. Missing ObjectToSave`);
        }

        const payload: SaveToCacheRequest<TBody> = {
            RestaurantCode: resCode,
            ObjectType: objectType,
            ExpirySeconds: expirySeconds,
            Body: objectToSave
        };

        await postJson<void, SaveToCacheRequest<TBody>>("/Cache/SaveToCache", payload);
    },

    /**
     * Get something from the session cache and deserialize it as type T.
     * @returns The cached value or null if not found.
     */
    async GetFromCache<T = unknown>(
        resCode: string,
        objectType: string
    ): Promise<T | null>
    {

        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to get from cache. Missing ResCode`);
        }

        if (objectType.trim() === "" || !objectType || objectType === null || objectType === undefined)
        {
            throw new Error(`Failed to get from cache. Missing ObjectType`);
        }

        if (objectType.trim() === "RestaurantDetails")
        {
           return this.GetResConfigFromCache(resCode) 
        }

        if (objectType.trim() === "Menu")
        {
           return this.GetMenuFromCache(resCode) 
        }

        if (objectType.trim() === "UserInfo")
        {
           return this.GetUserInfoFromCache(resCode) 
        }

        if (objectType.trim() === "UserProfile")
        {
           return this.GetUserProfileFromCache(resCode) 
        }

        const payload: CacheKeyRequest = {
            RestaurantCode: resCode,
            ObjectType: objectType
        };

        const response = await fetch("/Cache/GetFromCacheRaw", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(payload)
        });

        if (response.status === 404) {
            return null;
        }

        if (!response.ok) {
            throw new Error(
                `Failed to get cache for ${resCode} (${objectType}); status ${response.status}`
            );
        }

        const data = (await response.json()) as T;
        return data;
    },

    /**
    * Get a Restaurant Config from cache
    * @param resCode
    * @returns The cached value or null if not found.
    */
    async GetResConfigFromCache(resCode: string)
    {

        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to get ResConfig from cache. Missing ResCode`);
        }

        const response = await fetch("/Cache/GetResConfigFromCache", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(resCode)
        });

        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to get restaurnt config for ${resCode}`);

        return await response.json();
    },

    /**
    * Get a Restaurant Menu from cache
    * @param resCode
    * @returns The cached value or null if not found.
    */
    async GetMenuFromCache(resCode: string)
    {

        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to get Menu from cache. Missing ResCode`);
        }

        const response = await fetch("/Cache/GetMenuFromCache", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(resCode)
        });

        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to get menu for ${resCode}`);

        return await response.json();
    },

    /**
    * Get a logged in User Info from cache
    * @param resCode
    * @returns The cached value or null if not found.
    */
    async GetUserInfoFromCache(resCode: string)
    {

        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to get User Info from cache. Missing ResCode`);
        }

        const response = await fetch("/Cache/GetUserInfoFromCache", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(resCode)
        });

        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to get user info for ${resCode}`);

        return await response.json();
    },

    /**
    * Get a logged in User Profile from cache
    * @param resCode
    * @returns The cached value or null if not found.
    */
    async GetUserProfileFromCache(resCode: string)
    {
        if (resCode.trim() === "" || !resCode || resCode === null || resCode === undefined)
        {
            throw new Error(`Failed to get User Profile from cache. Missing ResCode`);
        }

        const response = await fetch("/Cache/GetUserProfileFromCache", {
            method: "POST",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(resCode)
        });

        if (response.status === 404) return null;
        if (!response.ok) throw new Error(`Failed to get user profile for ${resCode}`);

        return await response.json();
    },

    /**
     * Remove a specific cache entry (shared or per-user).
     */
    async DeleteFromCache(
        resCode: string,
        objectType: string
    ): Promise<void> {

        const payload: CacheKeyRequest = {
            RestaurantCode: resCode,
            ObjectType: objectType
        };

        await postJson<void, CacheKeyRequest>("/Cache/DeleteFromCache", payload);
    },
};

if (typeof window !== "undefined") {
    (window as any).CacheClient = CacheClient;
}